import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, FileText } from 'lucide-react';
import { Client } from '@/types/client';
import api from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { customerSupportService } from '@/services/customerSupportService';
import { format } from 'date-fns';

export interface ContratoVariaveis {
  plano_contratado?: string;
  quantidade_atendentes?: number;
  quantidade_contatos?: number;
  franquia_contatos?: number;
  valor_mensalidade?: number;
  valor_start?: number;
  custo_contato_adicional?: string;
  custo_usuario_adicional?: string;
  data_vencimento_1o?: string;
}

interface EmitirContratoModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
  onSuccess?: () => void;
}

function formatBRLInput(value: string): string {
  const n = value.replace(/\D/g, '');
  if (!n) return '';
  const i = parseInt(n, 10) / 100;
  return i.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseBRLToNumber(s: string): number | null {
  const clean = s.replace(/\D/g, '');
  if (!clean) return null;
  const n = parseInt(clean, 10) / 100;
  return isNaN(n) ? null : n;
}

const EmitirContratoModal: React.FC<EmitirContratoModalProps> = ({
  isOpen,
  onClose,
  client,
  onSuccess,
}) => {
  const [planoContratado, setPlanoContratado] = useState('');
  const [quantidadeAtendentes, setQuantidadeAtendentes] = useState('');
  const [quantidadeContatos, setQuantidadeContatos] = useState('');
  const [franquiaContatos, setFranquiaContatos] = useState('2000');
  const [valorMensalidade, setValorMensalidade] = useState('');
  const [valorStart, setValorStart] = useState('');
  const [custoContatoAdicional, setCustoContatoAdicional] = useState('0,09');
  const [custoUsuarioAdicional, setCustoUsuarioAdicional] = useState('34,90');
  const [dataVencimento1o, setDataVencimento1o] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [showKickoffConfirm, setShowKickoffConfirm] = useState(false);
  const [addingToKickoff, setAddingToKickoff] = useState(false);

  const hoje = new Date();
  const primeiroVencimento = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 10);

  useEffect(() => {
    if (isOpen) {
      setDataVencimento1o(format(primeiroVencimento, 'yyyy-MM-dd'));
    }
  }, [isOpen, client?.id]);

  useEffect(() => {
    if (isOpen && client?.id) {
      setLoadingConfig(true);
      api
        .get<{ config?: Partial<ContratoVariaveis> }>(`/client-products/client/${client.id}`)
        .then((res) => {
          const c = res.config;
          if (c?.plano_contratado) setPlanoContratado(c.plano_contratado);
          if (c?.quantidade_atendentes != null) setQuantidadeAtendentes(String(c.quantidade_atendentes));
          if (c?.quantidade_contatos != null) setQuantidadeContatos(String(c.quantidade_contatos));
          if (c?.valor_mensalidade != null)
            setValorMensalidade(Number(c.valor_mensalidade).toLocaleString('pt-BR', { minimumFractionDigits: 2 }));
          if (c?.valor_start != null)
            setValorStart(Number(c.valor_start).toLocaleString('pt-BR', { minimumFractionDigits: 2 }));
        })
        .catch(() => {})
        .finally(() => setLoadingConfig(false));
    }
  }, [isOpen, client?.id]);

  const handleGenerate = async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';
      const res = await fetch(`${API_BASE}/contracts/generate/${client.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plano_contratado: planoContratado.trim() || undefined,
          quantidade_atendentes: quantidadeAtendentes ? parseInt(quantidadeAtendentes, 10) : undefined,
          quantidade_contatos: quantidadeContatos ? parseInt(quantidadeContatos, 10) : undefined,
          franquia_contatos: franquiaContatos ? parseInt(franquiaContatos.replace(/\D/g, ''), 10) : undefined,
          valor_mensalidade: parseBRLToNumber(valorMensalidade),
          valor_start: parseBRLToNumber(valorStart),
          custo_contato_adicional: custoContatoAdicional.trim() || undefined,
          custo_usuario_adicional: custoUsuarioAdicional.trim() || undefined,
          data_vencimento_1o: dataVencimento1o || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Erro ao gerar contrato: ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cnpjPart = client.company_tax_id?.replace(/\D/g, '').slice(0, 14) || String(client.id);
      link.setAttribute('download', `contrato_${cnpjPart}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowKickoffConfirm(true);
    } catch (err: any) {
      toast({
        title: 'Erro ao gerar contrato',
        description: err?.message ?? 'Ocorreu um erro ao gerar o PDF.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKickoffConfirm = async (add: boolean) => {
    if (add && client?.id) {
      setAddingToKickoff(true);
      try {
        await customerSupportService.createImplementation({ client_id: client.id });
        toast({
          title: 'Cliente adicionado à implantação',
          description: 'O cliente foi incluído na etapa Kick-off do kanban de implantação.',
        });
      } catch (err: any) {
        const status = err?.response?.status;
        const msg = err?.response?.data?.error ?? err?.message ?? 'Erro ao adicionar à implantação.';
        toast({
          title: status === 409 ? 'Já existe implantação' : 'Erro',
          description: status === 409 ? 'Este cliente já possui uma implantação em andamento.' : msg,
          variant: status === 409 ? 'default' : 'destructive',
        });
      } finally {
        setAddingToKickoff(false);
        setShowKickoffConfirm(false);
        onSuccess?.();
        onClose();
      }
    } else {
      setShowKickoffConfirm(false);
      onSuccess?.();
      onClose();
    }
  };

  if (!client) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Emitir contrato
          </DialogTitle>
          <DialogDescription>
            Preencha as informações do contrato para {client.legal_name || client.trade_name}. Os dados do cliente (CNPJ, endereço etc.) serão preenchidos automaticamente.
          </DialogDescription>
        </DialogHeader>

        {loadingConfig ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="plano">Plano contratado</Label>
                <Input
                  id="plano"
                  placeholder="ex: Plano 15-2k"
                  value={planoContratado}
                  onChange={(e) => setPlanoContratado(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="atendentes">Atendentes</Label>
                <Input
                  id="atendentes"
                  type="number"
                  min="1"
                  placeholder="1"
                  value={quantidadeAtendentes}
                  onChange={(e) => setQuantidadeAtendentes(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contatos">Contatos (mil)</Label>
                <Input
                  id="contatos"
                  type="number"
                  min="1"
                  placeholder="1"
                  value={quantidadeContatos}
                  onChange={(e) => setQuantidadeContatos(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="franquia">Franquia contatos/mês</Label>
                <Input
                  id="franquia"
                  placeholder="2000"
                  value={franquiaContatos}
                  onChange={(e) => setFranquiaContatos(e.target.value.replace(/\D/g, ''))}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mensalidade">Valor da mensalidade</Label>
                <Input
                  id="mensalidade"
                  placeholder="0,00"
                  value={valorMensalidade}
                  onChange={(e) => setValorMensalidade(formatBRLInput(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="valor-start">Taxa de Implantação / Setup</Label>
                <Input
                  id="valor-start"
                  placeholder="0,00"
                  value={valorStart}
                  onChange={(e) => setValorStart(formatBRLInput(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="custo-contato">Custo por contato adicional (R$)</Label>
                <Input
                  id="custo-contato"
                  placeholder="0,09"
                  value={custoContatoAdicional}
                  onChange={(e) => setCustoContatoAdicional(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="custo-usuario">Custo por usuário adicional (R$)</Label>
                <Input
                  id="custo-usuario"
                  placeholder="34,90"
                  value={custoUsuarioAdicional}
                  onChange={(e) => setCustoUsuarioAdicional(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="vencimento">Data 1º vencimento</Label>
              <Input
                id="vencimento"
                type="date"
                value={dataVencimento1o}
                onChange={(e) => setDataVencimento1o(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleGenerate} disabled={loading || loadingConfig}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Gerando...
              </>
            ) : (
              'Gerar PDF'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={showKickoffConfirm} onOpenChange={() => {}}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Adicionar cliente à implantação</AlertDialogTitle>
            <AlertDialogDescription>
              Adicionar cliente a etapa Kick-off em implantação?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => handleKickoffConfirm(false)}
              disabled={addingToKickoff}
            >
              Não
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleKickoffConfirm(true)}
              disabled={addingToKickoff}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {addingToKickoff ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Adicionando...
                </>
              ) : (
                'Sim'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default EmitirContratoModal;
